/// <amd-module name="VSS/Platform/StartRightExtension" />

import { IVssPageContext, IVssService, ServiceOptions, Services, VssService } from "./Context";
import { onClickFPS } from "./FPS";
import { IVssNavigationService } from "./Navigation";
import { IVssTelemetryService } from "./Telemetry";
import * as CIConstants from 'CustomerIntelligenceConstants'

/**
 * Service for integration with Start Right extension.
 * Start Right is a 1st party extension that encourages users to create compliant Git repositories and Pipelines.
 * If the extension is installed, we replace default ADO experience with it.
 */
export interface IStartRightExtensionService extends IVssService {
    /**
     * If Start Right extension is installed, navigates to the Create Repository page of Start Right.
     * Returns true if SR is available and redirect was initiated, otherwise false which means that 
     * the caller should fall back to default behavior.
     */
    navigateToCreateRepository(pageContext: IVssPageContext): boolean;

    /**
     * If Start Right extension is installed, returns the URL of the Create Pipeline page of Start Right,
     * otherwise undefined which means that the caller should fall back to default behavior.
     */
    getCreatePipelineUrl(pageContext: IVssPageContext): string | undefined;

    /**
     * If Start Right extension is installed, returns the URL of the Create Pipeline page of Start Right,
     * otherwise undefined which means that the caller should fall back to default behavior.
     */
    getCreatePipelineUrlByRepository(pageContext: IVssPageContext, repositoryId: string, repositoryName: string, refName: string): string | undefined;

    /**
     * If Start Right extension is installed, returns the URL of the Create Pipeline page of Start Right,
     * otherwise undefined which means that the caller should fall back to default behavior.
     */
    getCreatePipelineUrlByPipelineFolder(pageContext: IVssPageContext, pipelineFolderPath: string): string | undefined;
}

class StartRightExtensionService extends VssService implements IStartRightExtensionService {
    navigateToCreateRepository(pageContext: IVssPageContext): boolean {
        const baseUrl = this.getStartRightBaseUrl(pageContext);
        if (!baseUrl) {
            return false;
        }

        try {
            const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService.publishEvent(CIConstants.START_RIGHT_AREA, 
                                        CIConstants.START_RIGHT_CREATE_REPO_FEATURE, 
                                        {}); 
        } catch {
            //ignore
        }

        const href = baseUrl + "#repository";
        onClickFPS(pageContext, href, true);
        return true;
    }

    getCreatePipelineUrl(pageContext: IVssPageContext): string | undefined {
        const baseUrl = this.getStartRightBaseUrl(pageContext);
        if (!baseUrl) {
            return undefined;
        }

        try {
            const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                    telemetryService.publishEvent(CIConstants.START_RIGHT_AREA, 
                                                CIConstants.START_RIGHT_CREATE_PIPELINE_URL_FEATURE, 
                                                {});  
        } catch {
            //ignore
        }

        return baseUrl + "#pipeline";
    }

    getCreatePipelineUrlByRepository(pageContext: IVssPageContext, repositoryId: string, repositoryName: string, refName: string): string | undefined {
        const baseUrl = this.getStartRightBaseUrl(pageContext);
        if (!baseUrl) {
            return undefined;
        }

        try {
            const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService.publishEvent(CIConstants.START_RIGHT_AREA,  
                                        CIConstants.START_RIGHT_CREATE_PIPELINE_URL_REPOSITORY_FEATURE, 
                                        {
                                            "RepositoryId": repositoryId,
                                            "RepositoryName": repositoryName,
                                            "RefName": refName
                                        });                 
        } catch {
            //ignore
        }

        const queryString =
            "?sourceProvider=TfsGit" +
            `&repositoryId=${encodeURIComponent(repositoryId)}` +
            `&repositoryName=${encodeURIComponent(repositoryName)}` +
            `&branch=${encodeURIComponent(refName)}` +
            "&sourceProviderComplete=true";
        const hash = "#pipeline";
        return baseUrl + queryString + hash;
    }

    getCreatePipelineUrlByPipelineFolder(pageContext: IVssPageContext, pipelineFolderPath: string): string | undefined {
        const baseUrl = this.getStartRightBaseUrl(pageContext);
        if (!baseUrl) {
            return undefined;
        }

        try {
            const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService.publishEvent(CIConstants.START_RIGHT_AREA, 
                                        CIConstants.START_RIGHT_CREATE_PIPELINE_URL_FOLDER_FEATURE, 
                                        {
                                            "PipelineFolderPath": pipelineFolderPath
                                        }); 
        } catch {
            //ignore
        }

        const queryString = `?pipelinePath=${encodeURIComponent(pipelineFolderPath)}`;
        const hash = "#pipeline";
        return baseUrl + queryString + hash;
    }

    private getStartRightBaseUrl(pageContext: IVssPageContext): string | undefined {
        const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const startRightExtensionNavigation = navigationService.getNavigation("ms-1es.startright.startright-group");
        if (!startRightExtensionNavigation) {
            return undefined;
        }

        return startRightExtensionNavigation.href;
    }
}

Services.add("IStartRightExtensionService", { serviceFactory: StartRightExtensionService, options: ServiceOptions.Startup });
